import React, { memo, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { useTranslation } from 'react-i18next'

import { ReactComponent as TutorialImage } from '../../assets/svg/TutorialModalImage.svg'
import { ReactComponent as ExitCross } from '../../assets/svg/ExitCross.svg'
import RocketImage from '../../assets/images/RocketImage.png'
import { useUser } from '../../auth'
import { fileSystemUrl } from '../../constants/Connections'
import { formatTwoDigitNumber } from '../../utils/util'
import '../../styles/TutorialModal.css'


const TutorialModal = memo(({ modalIsOpen, announcement, onInteraction }) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const [currentStep, setCurrentStep] = useState(1)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [imageFailed, setImageFailed] = useState(false)
    const steps = announcement?.steps || []
    const totalSteps = steps.length

    useEffect(() => {
        Modal.setAppElement('#root')
    }, [])

    useEffect(() => {
        if (modalIsOpen) {
            setCurrentStep(1)
            setSaving(false)
            setError('')
            setImageFailed(false)
        }
    }, [modalIsOpen, announcement?.id])

    const goNextStep = () => {
        if (currentStep < totalSteps) {
            setImageFailed(false)
            setCurrentStep(currentStep + 1)
        } else {
            persistInteraction('completed')
        }
    }

    const goPreviousStep = () => {
        if (currentStep > 1) {
            setImageFailed(false)
            setCurrentStep(currentStep - 1)
        }
    }

    const persistInteraction = async (interactionType) => {
        if (saving) return
        if (interactionType === 'dismissed') {
            onInteraction(interactionType, currentStep)
            return
        }
        setSaving(true)
        setError('')
        const saved = await onInteraction(interactionType, currentStep)
        if (!saved) {
            setError(t('announcementDismissError', 'We could not save your response. Please try again.'))
            setSaving(false)
        }
    }

    if (!announcement || !totalSteps) return null

    const currentStepData = steps[currentStep - 1]
    const isLast = currentStep === totalSteps
    const imageUrl = currentStepData.image?.storage_name
        ? `${fileSystemUrl}/${currentStepData.image.storage_name}?token=${user?.token}`
        : ''

    return (
        <Modal
            isOpen={modalIsOpen}
            onRequestClose={() => persistInteraction('dismissed')}
            shouldCloseOnOverlayClick={!saving}
            shouldCloseOnEsc={!saving}
            contentLabel="Feature announcement"
            className="tutorial-modal-content"
            overlayClassName="tutorial-modal-overlay"
            style={{ content: { inset: 'auto' } }}
        >
            <div className="tutorial-modal">
                <div className="tutorial-left-side">
                    <TutorialImage />
                    <img className="tutorial-brand-image" src={RocketImage} alt="" />
                    {currentStepData.label && <p className="major-update">{currentStepData.label}</p>}
                    <p className="major-update-values">
                        {formatTwoDigitNumber(currentStep)}/{formatTwoDigitNumber(totalSteps)}
                    </p>
                </div>
                <div className="tutorial-right-side">
                    <button
                        type="button"
                        disabled={saving}
                        aria-label={t('closePopUp', 'Close')}
                        className="tutorial-exit-button"
                        onClick={() => persistInteraction('dismissed')}
                    >
                        <ExitCross />
                    </button>
                    <p className="tutorial-modal-title">{currentStepData.header_title}</p>
                    <p className="tutorial-modal-subtitle">{currentStepData.header_subtitle}</p>
                    <div className="tutorial-card">
                        <div className="tutorial-image">
                            {imageUrl && !imageFailed ? (
                                <img
                                    src={imageUrl}
                                    onError={() => setImageFailed(true)}
                                    alt={currentStepData.content_title}
                                    key={currentStep}
                                />
                            ) : (
                                <div className="tutorial-image-placeholder" />
                            )}
                        </div>
                        <p className="tutorial-title">{currentStepData.content_title}</p>
                        <p className="tutorial-description">{currentStepData.body_text}</p>
                    </div>
                    <div style={{ flex: 1 }} />
                    {error && <p className="tutorial-error" role="alert">{error}</p>}
                    <div className="tutorial-footer">
                        <button
                            type="button"
                            disabled={currentStep === 1 || saving}
                            onClick={goPreviousStep}
                            className="tutorial-previous-button"
                        >
                            {t('Previous')}
                        </button>
                        <button type="button" disabled={saving} onClick={goNextStep} className="tutorial-next-button">
                            {saving ? t('saving', 'Saving...') : isLast ? t('closePopUp') : t('Next')}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    )
})

export default TutorialModal
